import { Caret } from '@mobile/components/kartoy/caret'
import { KeyboardInput } from '@mobile/components/kartoy/keyboard-input'
import { PillButton } from '@mobile/components/kartoy/pill-button'
import type { RootRouteProps } from '@mobile/screens'
import { actionUserLogin, login, requestOtp } from '@shared-core/features/auth'
import { fetchCurrentUser, retryUserPhone, verifyUserPhone } from '@shared-core/features/backend'
import { useNotifications } from '@shared-core/features/notifications'
import { setUserPhone } from '@shared-core/features/settings'
import { useI18N } from '@shared-core/hooks/use-i18n'
import { navigation, PhoneEntryMode, type ScreensList, StackMode, useRoute } from '@shared-core/navigation'
import { useAppDispatch } from '@shared-core/store'
import { formatPhoneDisplay } from '@shared-core/utils/phone'
import { Header } from '@shared-ui/components/ui/header'
import { ScreenWrapperWithoutScroll } from '@shared-ui/components/ui/screen-wrappers/screen-wrapper'
import { GText } from '@shared-ui/components/ui/text'
import { useEffect, useRef, useState } from 'react'
import { View } from 'react-native'
import Animated, { BounceIn, FadeInDown } from 'react-native-reanimated'

import locales from './index.i18n.json'
import { useCreateStyles } from './styles'

const PHONE_CODE_LENGTH = 4
const CODE_FEEDBACK_DELAY = 650

// Цифра OTP: bounce-пружина при вводе, каретка — в первом пустом боксе.
type OtpDigitProps = {
  value: string
  isActive: boolean
  status: 'idle' | 'error' | 'success'
}

function OtpDigit({ value, isActive, status }: OtpDigitProps) {
  const styles = useCreateStyles()

  const content = value ? (
    <Animated.Text
      key={value}
      entering={BounceIn.duration(280)}
      style={[styles.digitValue, status === 'error' && styles.digitError, status === 'success' && styles.digitSuccess]}
    >
      {value}
    </Animated.Text>
  ) : undefined

  return (
    <Animated.View
      entering={FadeInDown.delay(120).duration(380)}
      style={[
        styles.digitBox,
        status === 'error' && styles.digitErrorBox,
        status === 'success' && styles.digitSuccessBox,
      ]}
    >
      {content ?? (isActive ? <Caret height={28} /> : <View />)}
    </Animated.View>
  )
}

export function ScreenEntryOtp() {
  const styles = useCreateStyles()
  const dispatch = useAppDispatch()
  const { t } = useI18N(locales)
  const route = useRoute<RootRouteProps<ScreensList.ENTRY_OTP>>()
  const { addToastSuccess, addToastError } = useNotifications()
  const [idempotency, setIdempotency] = useState(route.params?.idempotency ?? '')
  const [resendAvailableAt, setResendAvailableAt] = useState(route.params?.resendAvailableAt ?? 0)
  const [now, setNow] = useState(Date.now)
  const [isResending, setIsResending] = useState(false)
  const resendingRef = useRef(false)
  const verifyingRef = useRef(false)
  const retrySeconds = Math.max(0, Math.ceil((resendAvailableAt - now) / 1000))

  const [code, setCode] = useState('')
  const [status, setStatus] = useState<'idle' | 'error' | 'success'>('idle')
  const phone = route.params?.phone ?? ''
  const isBind = route.params?.mode === PhoneEntryMode.BIND
  const codeLength = PHONE_CODE_LENGTH
  const isComplete = code.length === codeLength
  const formattedPhone = formatPhoneDisplay(phone)

  useEffect(() => {
    if (resendAvailableAt <= Date.now()) return
    const timer = setInterval(() => setNow(Date.now()), 250)
    return () => clearInterval(timer)
  }, [resendAvailableAt])

  // Повторно отправляем код и запускаем новый срок ожидания из ответа сервера.
  const handleResend = async () => {
    if (resendingRef.current || verifyingRef.current || Date.now() < resendAvailableAt || status !== 'idle') return
    resendingRef.current = true
    setIsResending(true)
    setCode('')
    setStatus('idle')
    try {
      const result = isBind
        ? await dispatch(retryUserPhone(idempotency)).unwrap()
        : await dispatch(requestOtp({ phone: phone.replace(/\D/g, '') })).unwrap()

      if (!result) return

      if ('idempotency' in result) setIdempotency(result.idempotency)

      const sentAt = Date.now()
      setNow(sentAt)
      setResendAvailableAt(sentAt + result.retryAfter * 1000)
      addToastSuccess(t('resend-success'))
    } catch {
      addToastError(t('resend-error'))
    } finally {
      resendingRef.current = false
      setIsResending(false)
    }
  }

  useEffect(() => {
    if (!isComplete || status !== 'idle' || isResending) return

    // Проверяем код на бэкенде после ввода всех цифр.
    const timer = setTimeout(() => {
      // Отправляем код после паузы, чтобы не дергать API на каждый символ.
      void (async () => {
        if (verifyingRef.current) return
        verifyingRef.current = true
        try {
          if (isBind) {
            const verified = await dispatch(verifyUserPhone({ idempotency, otp: code })).unwrap()

            if (!verified) {
              setStatus('error')
              return
            }
            await dispatch(fetchCurrentUser()).unwrap()
          } else {
            await dispatch(login({ phone: phone.replace(/\D/g, ''), otp: code })).unwrap()
          }
          setStatus('success')
        } catch {
          setStatus('error')
        } finally {
          verifyingRef.current = false
        }
      })()
    }, 350)

    return () => clearTimeout(timer)
  }, [code, codeLength, dispatch, idempotency, isBind, isComplete, isResending, phone, status])

  // Даём увидеть результат проверки перед переходом или очисткой кода.
  useEffect(() => {
    if (status === 'idle') return

    const timer = setTimeout(() => {
      if (status === 'error') {
        setCode('')
        setStatus('idle')
        return
      }

      if (phone) dispatch(setUserPhone(phone))

      if (!isBind) dispatch(actionUserLogin())
      navigation.reset(StackMode.AUTHORIZED)
    }, CODE_FEEDBACK_DELAY)

    return () => clearTimeout(timer)
  }, [dispatch, isBind, phone, status])

  return (
    <ScreenWrapperWithoutScroll style={styles.container}>
      <Header isBackButton exitCross={false} />

      <Animated.View style={styles.content}>
        <Animated.View style={styles.topBlock}>
          <Animated.View entering={FadeInDown.delay(60).duration(420)} style={styles.titleBlock}>
            <GText fw={600} style={styles.title}>
              {t('title')}
            </GText>
            <GText style={styles.subtitle}>{t('subtitle', { phone: formattedPhone })}</GText>
          </Animated.View>

          <Animated.View entering={FadeInDown.delay(160).duration(420)} style={styles.codeBlock}>
            <Animated.View style={styles.digitsRow}>
              {Array.from({ length: codeLength }, (_, index) => (
                <OtpDigit key={index} value={code[index] ?? ''} isActive={index === code.length} status={status} />
              ))}
            </Animated.View>
          </Animated.View>
        </Animated.View>
      </Animated.View>

      {status !== 'success' && (
        <Animated.View entering={FadeInDown.duration(320)} style={styles.action}>
          <PillButton
            title={
              retrySeconds > 0
                ? t('resend-countdown', {
                    time: `${Math.floor(retrySeconds / 60)}:${String(retrySeconds % 60).padStart(2, '0')}`,
                  })
                : t('resend')
            }
            height={52}
            variant="secondary"
            disabled={retrySeconds > 0 || isResending || status !== 'idle'}
            onPress={() => void handleResend()}
          />
        </Animated.View>
      )}

      <KeyboardInput
        value={code}
        isDecimalKeyVisible={false}
        onChange={(next) => {
          if (verifyingRef.current || isResending || status !== 'idle') return
          setStatus('idle')
          setCode(next.replace(/\D/g, '').slice(0, codeLength))
        }}
        inputProps={{ keyboardType: 'number-pad' }}
      />
    </ScreenWrapperWithoutScroll>
  )
}
